/**
 * The editor's state: the document, what is selected, undo/redo, and the local
 * draft (DESIGN §9.3).
 *
 * The draft is the reason there is no router blocker. The app mounts `<Routes>`
 * rather than a data router, so `useBlocker` is unavailable — and a warning
 * that work is about to be lost is worse than not losing it. Every change is
 * written to `localStorage` with the version it was based on, so reopening a
 * design offers the unsaved work back instead of mourning it.
 */

import { useCallback, useEffect, useMemo, useReducer } from "react";

import type { Block, BlockType, ImageBlock, Spec, Theme } from "./spec";
import { newBlock, settled } from "./spec";

/** Deep enough for a session's worth of edits, shallow enough to stay cheap. */
const HISTORY_LIMIT = 50;

export interface DraftEnvelope {
  spec: Spec;
  /** The saved version this draft was started from — a newer row wins. */
  baseVersion: number;
  savedAt: string;
}

export function draftKey(designId: string): string {
  return `qa.design.draft.${designId}`;
}

export function readDraft(designId: string): DraftEnvelope | null {
  try {
    const raw = localStorage.getItem(draftKey(designId));
    return raw ? (JSON.parse(raw) as DraftEnvelope) : null;
  } catch {
    return null;
  }
}

export function writeDraft(designId: string, envelope: DraftEnvelope): void {
  try {
    localStorage.setItem(draftKey(designId), JSON.stringify(envelope));
  } catch {
    /* private browsing — the draft simply will not survive a reload */
  }
}

export function clearDraft(designId: string): void {
  try {
    localStorage.removeItem(draftKey(designId));
  } catch {
    /* nothing to do */
  }
}

interface State {
  spec: Spec;
  selectedId: string | null;
  past: Spec[];
  future: Spec[];
  dirty: boolean;
}

export type Action =
  | { type: "reset"; spec: Spec; dirty?: boolean }
  | { type: "select"; id: string | null }
  | { type: "setTheme"; theme: Theme }
  | { type: "updateBlock"; id: string; patch: Record<string, unknown> }
  | { type: "replaceBlock"; id: string; block: Block }
  | { type: "addBlock"; blockType: BlockType }
  | { type: "insertBlock"; block: Block; at: number }
  | { type: "duplicateBlock"; id: string }
  | { type: "removeBlock"; id: string }
  | { type: "moveBlock"; id: string; delta: number }
  | { type: "reorderBlock"; id: string; to: number }
  | { type: "settleImage"; id: string; asset: string }
  | { type: "cancelImport"; id: string; inserted: boolean }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "saved"; spec: Spec };

function clone(spec: Spec): Spec {
  return JSON.parse(JSON.stringify(spec)) as Spec;
}

/** Push the current document onto the undo stack and drop the redo branch. */
function commit(state: State, spec: Spec, selectedId = state.selectedId): State {
  return {
    spec,
    selectedId,
    past: [...state.past, state.spec].slice(-HISTORY_LIMIT),
    future: [],
    dirty: true,
  };
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "reset":
      return {
        spec: action.spec,
        selectedId: null,
        past: [],
        future: [],
        dirty: action.dirty ?? false,
      };

    case "saved":
      return { ...state, spec: action.spec, dirty: false };

    case "select":
      return { ...state, selectedId: action.id };

    case "setTheme":
      return commit(state, { ...state.spec, theme: action.theme });

    case "updateBlock": {
      const blocks = state.spec.blocks.map((block) =>
        block.id === action.id ? ({ ...block, ...action.patch } as Block) : block,
      );
      return commit(state, { ...state.spec, blocks });
    }

    case "replaceBlock": {
      const blocks = state.spec.blocks.map((block) =>
        block.id === action.id ? action.block : block,
      );
      return commit(state, { ...state.spec, blocks });
    }

    case "addBlock": {
      const block = newBlock(action.blockType);
      const at = state.selectedId
        ? state.spec.blocks.findIndex((b) => b.id === state.selectedId) + 1
        : state.spec.blocks.length;
      const blocks = [...state.spec.blocks];
      blocks.splice(at, 0, block);
      return commit(state, { ...state.spec, blocks }, block.id);
    }

    case "insertBlock": {
      // One history step for one gesture: a dropped picture is undone by a
      // single ⌘Z, however many round trips it took to land.
      const blocks = [...state.spec.blocks];
      blocks.splice(Math.max(0, Math.min(action.at, blocks.length)), 0, action.block);
      return commit(state, { ...state.spec, blocks }, action.block.id);
    }

    case "duplicateBlock": {
      const index = state.spec.blocks.findIndex((b) => b.id === action.id);
      if (index < 0) return state;
      const source = state.spec.blocks[index];
      const copy = { ...clone({ v: 1, blocks: [source] }).blocks[0], id: freshId(state, source.id) };
      const blocks = [...state.spec.blocks];
      blocks.splice(index + 1, 0, copy);
      return commit(state, { ...state.spec, blocks }, copy.id);
    }

    case "removeBlock": {
      const blocks = state.spec.blocks.filter((block) => block.id !== action.id);
      if (blocks.length === state.spec.blocks.length) return state;
      return commit(state, { ...state.spec, blocks }, null);
    }

    case "moveBlock": {
      const index = state.spec.blocks.findIndex((b) => b.id === action.id);
      const target = index + action.delta;
      if (index < 0 || target < 0 || target >= state.spec.blocks.length) return state;
      const blocks = [...state.spec.blocks];
      const [block] = blocks.splice(index, 1);
      blocks.splice(target, 0, block);
      return commit(state, { ...state.spec, blocks }, block.id);
    }

    case "reorderBlock": {
      // `to` is the slot the insertion line was drawn in — 0..blocks.length,
      // counted before the block is lifted out, which is what the customer is
      // pointing at. Taking it out first shifts every slot after it down one.
      const from = state.spec.blocks.findIndex((b) => b.id === action.id);
      if (from < 0) return state;
      const blocks = [...state.spec.blocks];
      const [block] = blocks.splice(from, 1);
      const to = Math.max(0, Math.min(action.to > from ? action.to - 1 : action.to, blocks.length));
      if (to === from) return state;
      blocks.splice(to, 0, block);
      return commit(state, { ...state.spec, blocks }, block.id);
    }

    case "settleImage": {
      // The upload landed. Deliberately not a history step of its own: the
      // customer made one gesture, and undo should take the whole block back.
      const blocks = state.spec.blocks.map((block) => {
        if (block.id !== action.id || block.type !== "image") return block;
        const next: ImageBlock = { ...block, asset: action.asset };
        delete next.pendingSrc;
        return next;
      });
      return { ...state, spec: { ...state.spec, blocks } };
    }

    case "cancelImport": {
      // The upload failed, so the gesture never happened: the optimistic block
      // goes (or gets its old picture back), and the history entry the insert
      // pushed goes with it when it is still the one on top — an undo must not
      // have to step over a block the customer never saw arrive.
      const blocks = action.inserted
        ? state.spec.blocks.filter((block) => block.id !== action.id)
        : state.spec.blocks.map((block) => {
            if (block.id !== action.id || block.type !== "image") return block;
            const next: ImageBlock = { ...block };
            delete next.pendingSrc;
            return next;
          });
      const spec = { ...state.spec, blocks };
      const previous = state.past[state.past.length - 1];
      const undone = previous !== undefined && JSON.stringify(previous) === JSON.stringify(spec);
      return {
        ...state,
        spec,
        selectedId: action.inserted && state.selectedId === action.id ? null : state.selectedId,
        past: undone ? state.past.slice(0, -1) : state.past,
      };
    }

    case "undo": {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...state,
        spec: previous,
        past: state.past.slice(0, -1),
        future: [state.spec, ...state.future].slice(0, HISTORY_LIMIT),
        dirty: true,
      };
    }

    case "redo": {
      const [next, ...rest] = state.future;
      if (!next) return state;
      return {
        ...state,
        spec: next,
        past: [...state.past, state.spec].slice(-HISTORY_LIMIT),
        future: rest,
        dirty: true,
      };
    }
  }
}

/** `logo` → `logo_2`, bumped until it is free (ids must be unique, §4.1). */
function freshId(state: State, base: string): string {
  const taken = new Set(state.spec.blocks.map((block) => block.id));
  const stem = base.replace(/_\d+$/, "").slice(0, 28);
  for (let suffix = 2; suffix < 100; suffix += 1) {
    const candidate = `${stem}_${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${stem}_${Math.random().toString(36).slice(2, 6)}`;
}

export function useDesignEditor(designId: string, initial: Spec, baseVersion: number) {
  const [state, dispatch] = useReducer(reducer, {
    spec: initial,
    selectedId: null,
    past: [],
    future: [],
    dirty: false,
  });

  // Every change lands in localStorage, so closing the tab costs nothing.
  // `settled` because an object URL does not survive the reload that would
  // read this back — a half-uploaded picture is simply not part of the draft.
  useEffect(() => {
    if (!state.dirty) return;
    writeDraft(designId, {
      spec: settled(state.spec),
      baseVersion,
      savedAt: new Date().toISOString(),
    });
  }, [designId, baseVersion, state.spec, state.dirty]);

  useEffect(() => {
    if (!state.dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state.dirty]);

  const selected = useMemo(
    () => state.spec.blocks.find((block) => block.id === state.selectedId) ?? null,
    [state.spec.blocks, state.selectedId],
  );

  const act = useCallback((action: Action) => dispatch(action), []);

  return {
    ...state,
    selected,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    dispatch: act,
  };
}
